import { type ReactNode } from 'react';

interface FormFieldProps {
  label: string;
  error?: string | null;
  required?: boolean;
  hint?: string;
  children: ReactNode;
  htmlFor?: string;
}

/**
 * Reusable form field wrapper with label, error message, and optional hint.
 */
export function FormField({ label, error, required, hint, children, htmlFor }: FormFieldProps) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && (
          <span className="text-red-500 ml-0.5" aria-label="required">
            *
          </span>
        )}
      </label>
      {children}
      {error && (
        <p
          id={htmlFor ? `${htmlFor}-error` : undefined}
          className="text-xs text-red-500 mt-1 flex items-center gap-1"
          role="alert"
        >
          <svg className="w-3 h-3 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </p>
      )}
      {!error && hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string | null;
}

/**
 * Styled input that shows error state.
 */
export function FormInput({ error, className = '', id, ...props }: FormInputProps) {
  const errorId = error && id ? `${id}-error` : undefined;
  return (
    <input
      {...props}
      id={id}
      aria-describedby={errorId}
      aria-invalid={!!error}
      className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 transition-colors ${
        error
          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50/50'
          : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
      } ${className}`}
    />
  );
}

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: string | null;
}

/**
 * Styled select that shows error state.
 */
export function FormSelect({ error, className = '', children, ...props }: FormSelectProps) {
  return (
    <select
      {...props}
      className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 transition-colors ${
        error
          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50/50'
          : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
      } ${className}`}
    >
      {children}
    </select>
  );
}
